<template>
    <b-card>
        <div v-if="summaryStats.length > 0" class="row mb-2">
            <div v-for="(stat, index) in summaryStats" :key="index" class="col-md-3">
                <VStatsBox v-bind="stat" />
            </div>
        </div>
        <div class="row mb-2">
            <div class="col-md-4">
                <label><span class="font-weight-bold">Yakıt Tipi</span></label>
                <v-select v-model="selectedFuelType" :options="fuelTypeSelectItems" label="name" placeholder="Seçiniz..." clearable />
            </div>
            <div class="col-md-4">
                <label><span class="font-weight-bold">Şube</span></label>
                <v-select v-model="selectedBranch" :options="branchSelectItems" :disabled="!selectedFuelType" label="name" placeholder="Seçiniz..." clearable />
            </div>
            <div class="col-md-4">
                <label><span class="font-weight-bold">Depo</span></label>
                <v-select v-model="selectedDepot" :options="depotSelectItems" :disabled="!selectedBranch" label="DisplayName" placeholder="Seçiniz..." clearable />
            </div>
        </div>

        <hr class="mb-2" />

        <div v-if="!loading" class="row d-flex align-items-stretch">
            <div v-if="selectedFuelType && fuelTypeSummary" class="col-md-4 d-flex flex-column mb-1">
                <h4 class="text-center">Genel Durum</h4>
                <div class="border rounded p-2 flex-grow-1 position-relative">
                    <span :class="['badge', selectedFuelType.name === 'Motorin' ? 'badge-warning' : 'badge-primary']" class="position-absolute" style="top: 10px; right: 10px">
                        {{ selectedFuelType.name }}
                    </span>
                    <fuel-storage-gauge :summary-data="fuelTypeSummary" />
                </div>
            </div>

            <div v-if="selectedBranch && branchSummary" class="col-md-4 d-flex flex-column mb-1">
                <h4 class="text-center">{{ selectedBranch.name }} - Şube Durumu</h4>
                <div class="border rounded p-2 flex-grow-1 position-relative">
                    <span :class="['badge', selectedFuelType.name === 'Motorin' ? 'badge-warning' : 'badge-primary']" class="position-absolute" style="top: 10px; right: 10px">
                        {{ selectedFuelType.name }}
                    </span>
                    <fuel-storage-gauge :summary-data="branchSummary" />
                </div>
            </div>

            <div v-if="selectedDepot && depotSummary" class="col-md-4 d-flex flex-column mb-1">
                <h4 class="text-center">{{ selectedDepot.FuelStorageName }} - Depo Detayı</h4>
                <div class="border rounded p-2 flex-grow-1 position-relative">
                    <span :class="['badge', selectedFuelType.name === 'Motorin' ? 'badge-warning' : 'badge-primary']" class="position-absolute" style="top: 10px; right: 10px">
                        {{ selectedFuelType.name }}
                    </span>
                    <fuel-storage-gauge :summary-data="depotSummary" />
                </div>
            </div>
        </div>

        <div v-else class="text-center">
            <b-spinner />
            <p>Veriler yükleniyor...</p>
        </div>
    </b-card>
</template>

<script>
import { BCard, BSpinner } from "bootstrap-vue";
import vSelect from "vue-select";
import FuelStorageApi from "@/api/fuel-storage";
import FuelStorageGauge from "./components/FuelStorageGauge.vue";
import VStatsBox from "@/components/VStatsBox.vue";

export default {
    name: "FuelStorageOccupancyReportView",
    components: {
        BCard,
        BSpinner,
        vSelect,
        FuelStorageGauge,
        VStatsBox,
    },
    data() {
        return {
            loading: false,
            allStorages: [],
            selectedFuelType: null,
            selectedBranch: null,
            selectedDepot: null,
        };
    },
    computed: {
        currentlyFilteredStorages() {
            if (!this.selectedFuelType) return [];

            let items = this.allStorages.filter((depot) => depot.FuelTypeName === this.selectedFuelType.name);

            if (this.selectedBranch) {
                items = items.filter((depot) => depot.BranchName === this.selectedBranch.name);
            }
            if (this.selectedDepot) {
                items = items.filter((depot) => depot.FuelStorageId === this.selectedDepot.FuelStorageId);
            }
            return items;
        },
        summaryStats() {
            return this.generateSummaryStats(this.currentlyFilteredStorages);
        },
        fuelTypeSelectItems() {
            const uniqueFuelTypes = [...new Set(this.allStorages.map((depot) => depot.FuelTypeName))];
            return uniqueFuelTypes.map((name) => ({ name: name }));
        },

        branchSelectItems() {
            if (!this.selectedFuelType) return [];
            const filteredByFuel = this.allStorages.filter((depot) => depot.FuelTypeName === this.selectedFuelType.name);
            const uniqueBranches = [...new Set(filteredByFuel.map((depot) => depot.BranchName))];
            return uniqueBranches.map((name) => ({ id: name, name: name }));
        },

        depotSelectItems() {
            if (!this.selectedBranch || !this.selectedFuelType) return [];
            return this.allStorages
                .filter((depot) => depot.BranchName === this.selectedBranch.name && depot.FuelTypeName === this.selectedFuelType.name)
                .map((depot) => ({
                    ...depot,
                    DisplayName: `${depot.FuelStorageName} [${(depot.Capacity || 0).toLocaleString("tr-TR")} lt]`,
                }));
        },

        fuelTypeSummary() {
            if (!this.selectedFuelType) return null;
            const filtered = this.allStorages.filter((depot) => depot.FuelTypeName === this.selectedFuelType.name);
            return this.calculateSummary(filtered);
        },

        branchSummary() {
            if (!this.selectedBranch || !this.selectedFuelType) return null;
            const filtered = this.allStorages.filter((depot) => depot.BranchName === this.selectedBranch.name && depot.FuelTypeName === this.selectedFuelType.name);
            return this.calculateSummary(filtered);
        },

        depotSummary() {
            if (!this.selectedDepot) return null;
            return this.calculateSummary([this.selectedDepot]);
        },
    },

    watch: {
        selectedFuelType() {
            this.selectedBranch = null;
            this.selectedDepot = null;
        },
        selectedBranch() {
            this.selectedDepot = null;
        },
    },

    created() {
        this.fetchData();
    },

    methods: {
        generateSummaryStats(data) {
            if (!data || data.length === 0) {
                return [];
            }

            const totalCapacity = data.reduce((sum, s) => sum + s.Capacity, 0);
            const totalStock = data.reduce((sum, s) => sum + s.CurrentStock, 0);
            const occupancyRate = totalCapacity > 0 ? (totalStock / totalCapacity) * 100 : 0;

            return [
                { text: "Toplam Depo Sayısı", value: data.length, icon: "inventory_2", variant: "primary" },
                { text: "Toplam Kapasite [lt]", value: totalCapacity.toLocaleString("tr-TR"), icon: "oil_barrel", variant: "success" },
                { text: "Mevcut Stok [lt]", value: totalStock.toLocaleString("tr-TR"), icon: "opacity", variant: "info" },
                { text: "Doluluk Oranı [%]", value: occupancyRate.toLocaleString("tr-TR", { minimumFractionDigits: 1, maximumFractionDigits: 1 }), icon: "speed", variant: "warning" },
            ];
        },
        fetchData() {
            this.loading = true;
            FuelStorageApi.FuelStorageReport.GetFuelStorageStockLevelReport({})
                .then((response) => {
                    if (response.data.Success) {
                        this.allStorages = response.data.Data;
                    } else {
                        console.error("API'den veri alınamadı:", response.data.Message);
                    }
                })
                .catch((error) => {
                    console.error("API isteği başarısız:", error);
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        calculateSummary(storages) {
            if (!storages || storages.length === 0) return null;

            const totals = storages.reduce(
                (acc, depot) => {
                    acc.capacity += depot.Capacity;
                    acc.quantity += depot.CurrentStock;
                    acc.minStockLevel += depot.MinStockLevel;
                    acc.criticalStockLevel += depot.CriticalStockLevel;
                    return acc;
                },
                { capacity: 0, quantity: 0, minStockLevel: 0, criticalStockLevel: 0 }
            );

            const emptySpace = totals.capacity - totals.quantity;

            return {
                ...totals,
                status: status,
                percentage: totals.capacity > 0 ? (totals.quantity / totals.capacity) * 100 : 0,
                emptySpace: emptySpace,

                capacityStr: totals.capacity.toLocaleString("tr-TR"),
                quantityStr: totals.quantity.toLocaleString("tr-TR"),
                emptySpaceStr: emptySpace.toLocaleString("tr-TR"),

                percentageStr: (totals.capacity > 0 ? (totals.quantity / totals.capacity) * 100 : 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
                emptySpacePercentageStr: (totals.capacity > 0 ? (emptySpace / totals.capacity) * 100 : 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
            };
        },
    },
};
</script>
